/**
 * Invite Link Manager
 * 
 * Component for creating and managing invite links for designee onboarding.
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Tooltip,
  CircularProgress,
  Divider,
} from '@mui/material';
import { contentCardSx } from '../../../../../styles/standaloneLayoutTokens';
import AddLinkIcon from '@mui/icons-material/AddLink';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { chartmindAdminService } from '../../../../../services/chartmind';

// Generate the full invite URL
const getInviteUrl = (code) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/join/${code}`;
};

const InviteLinkManager = ({
  adminId,
  inviteLinks = [],
  onInviteLinkChange,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [copied, setCopied] = useState(null);
  const [error, setError] = useState('');
  
  // New link options
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [expiresInDays, setExpiresInDays] = useState('');
  const [maxUses, setMaxUses] = useState('');

  // Create new invite link
  const handleCreateLink = useCallback(async () => {
    setIsCreating(true);
    setError('');
    
    try {
      await chartmindAdminService.createInviteLink(adminId, {
        requiresApproval,
        expiresInDays: expiresInDays ? parseInt(expiresInDays) : null,
        maxUses: maxUses ? parseInt(maxUses) : null,
      });
      
      setShowCreateDialog(false);
      setRequiresApproval(true);
      setExpiresInDays('');
      setMaxUses('');
      onInviteLinkChange?.();
    } catch (err) {
      console.error('[InviteLinkManager] Error creating link:', err);
      setError('Failed to create invite link. Please try again.');
    } finally {
      setIsCreating(false);
    }
  }, [adminId, requiresApproval, expiresInDays, maxUses, onInviteLinkChange]);

  // Delete invite link
  const handleDeleteLink = useCallback(async (code) => {
    setIsDeleting(code);
    
    try {
      await chartmindAdminService.deleteInviteLink(adminId, code);
      onInviteLinkChange?.();
    } catch (err) {
      console.error('[InviteLinkManager] Error deleting link:', err);
      setError('Failed to delete invite link. Please try again.');
    } finally {
      setIsDeleting(null);
    }
  }, [adminId, onInviteLinkChange]);

  // Copy link to clipboard
  const handleCopyLink = useCallback(async (code) => {
    const url = getInviteUrl(code);
    
    try {
      await navigator.clipboard.writeText(url);
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('[InviteLinkManager] Error copying to clipboard:', err);
    }
  }, []);

  // Check if link is expired
  const isExpired = (link) => {
    if (!link.expiresAt) return false;
    return new Date(link.expiresAt) < new Date();
  };

  // Check if link is exhausted (max uses reached)
  const isExhausted = (link) => {
    if (link.maxUses === null) return false;
    return link.usedCount >= link.maxUses;
  };

  // Get link status
  const getLinkStatus = (link) => {
    if (isExpired(link)) return { label: 'Expired', color: 'error' };
    if (isExhausted(link)) return { label: 'Limit Reached', color: 'warning' };
    return { label: 'Active', color: 'success' };
  };

  return (
    <Box>
      {/* Create link button */}
      <Paper elevation={0} sx={{ ...contentCardSx, p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6">Invite Links</Typography>
            <Typography variant="body2" color="text.secondary">
              Create shareable links for team members to join your organization.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddLinkIcon />}
            onClick={() => setShowCreateDialog(true)}
          >
            Create Link
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Existing links */}
        {inviteLinks.length === 0 ? (
          <Alert severity="info">
            No invite links created yet. Create a link to share with potential team members.
          </Alert>
        ) : (
          <List>
            {inviteLinks.map((link) => {
              const status = getLinkStatus(link);
              const expired = isExpired(link);
              const exhausted = isExhausted(link);
              const inactive = expired || exhausted;
              
              return (
                <ListItem
                  key={link.code}
                  sx={{
                    bgcolor: inactive ? 'grey.100' : 'grey.50',
                    borderRadius: 1,
                    mb: 1,
                    opacity: inactive ? 0.7 : 1,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, mr: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <LinkIcon fontSize="small" color={inactive ? 'disabled' : 'primary'} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          color: inactive ? 'text.disabled' : 'text.primary',
                        }}
                      >
                        {link.code}
                      </Typography>
                      <Chip 
                        label={status.label} 
                        size="small" 
                        color={status.color}
                      />
                      {link.requiresApproval ? (
                        <Tooltip title="Requires approval">
                          <LockIcon fontSize="small" color="action" />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Auto-approve">
                          <LockOpenIcon fontSize="small" color="action" />
                        </Tooltip>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        Created: {new Date(link.createdAt).toLocaleDateString()}
                      </Typography>
                      {link.expiresAt && (
                        <Typography variant="caption" color={expired ? 'error.main' : 'text.secondary'}>
                          <AccessTimeIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                          Expires: {new Date(link.expiresAt).toLocaleDateString()}
                        </Typography>
                      )}
                      {link.maxUses !== null && (
                        <Typography variant="caption" color={exhausted ? 'warning.main' : 'text.secondary'}>
                          <GroupIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                          Uses: {link.usedCount}/{link.maxUses}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  <ListItemSecondaryAction>
                    {isDeleting === link.code ? (
                      <CircularProgress size={24} />
                    ) : (
                      <>
                        <Tooltip title={copied === link.code ? 'Copied!' : 'Copy link'}>
                          <IconButton
                            onClick={() => handleCopyLink(link.code)}
                            disabled={inactive}
                          >
                            <ContentCopyIcon 
                              color={copied === link.code ? 'success' : 'action'} 
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete link">
                          <IconButton
                            onClick={() => handleDeleteLink(link.code)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Create link dialog */}
      <Dialog 
        open={showCreateDialog} 
        onClose={() => setShowCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Invite Link</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Approval setting */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={requiresApproval}
                    onChange={(e) => setRequiresApproval(e.target.checked)}
                  />
                }
                label={requiresApproval ? 'Require approval' : 'Auto-approve'}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {requiresApproval
                  ? 'You will need to manually approve each person who uses this link.'
                  : 'Anyone who uses this link will automatically be added to your team.'}
              </Typography>
            </Box>

            <Divider />

            {/* Expiration */}
            <TextField
              label="Expires After (days)"
              type="number"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
              placeholder="Never expires"
              InputProps={{
                inputProps: { min: 1 },
              }}
              helperText="Leave empty for no expiration"
            />

            {/* Max uses */}
            <TextField
              label="Maximum Uses"
              type="number"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Unlimited"
              InputProps={{
                inputProps: { min: 1 },
              }}
              helperText="Leave empty for unlimited uses"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateLink}
            disabled={isCreating}
            startIcon={isCreating ? <CircularProgress size={20} /> : <AddLinkIcon />}
          >
            Create Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InviteLinkManager;
